import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { DebouncedFunc } from "lodash";

export type AsyncFunction<P extends any[], R> = (...args: P) => Promise<R>;

export type UseTaskReturn<P extends any[], R> = TaskState<R> & {
    isInitialCall: boolean;
    setCountOfCalls: (countOfCalls: number) => void;
    perform: (...args: P) => Promise<R | null>;
    debouncedPerform: (...args: P) => Promise<R | null>;
    setValue: (value: R | null) => void;
};

function useDebounce<P extends any[], R>(
    callback: (...args: P) => Promise<R>,
    delay: number
): (...args: P) => Promise<R> {
    const ref = useRef<(...args: P) => Promise<R>>();

    useEffect(() => {
        ref.current = callback;
    }, [callback]);

    return useMemo(() => {
        const debouncedFunc = async (...args: P): Promise<R> => {
            if (ref.current) {
                return ref.current(...args);
            }
            throw new Error("Callback function not set");
        };

        const debounced: DebouncedFunc<(...args: P) => Promise<R>> = debounce(debouncedFunc, delay);

        return (...args: P) => new Promise<R>((resolve, reject) => {
            debounced(...args)?.then(resolve).catch(reject);
        });
    }, [delay]);
}

type TaskState<T> = {
    isRunning: boolean;
    countOfCalls: number;
    value: T | null;
}

/// TODO: We need to add other modiefiers as restartable, enqueueable, keepLatest, etc.
/// base on the ember-concurance library https://ember-concurrency.com/docs/task-concurrency/
/// It is not necessary to implement all of them, but it would be nice to have some of them.
export type TaskModifiers<T> = {
    debounceDelay: number;
    initialValue: T | null;
    /// The drop modifier drops tasks that are .perform() ed while another is already running.
    drop: boolean;
    keepLatest: boolean;
}

const defaultModifiers: TaskModifiers<null> = {
    debounceDelay: 600,
    drop: false,
    keepLatest: false,
    initialValue: null,
};

export const useTask = <P extends any[], R>(asyncFunction: AsyncFunction<P, R>, modifiersPartial: Partial<TaskModifiers<R>> = {}): UseTaskReturn<P, R> => {
    const { debounceDelay, drop, initialValue, keepLatest } = { ...defaultModifiers, ...modifiersPartial };

    const [taskState, setTaskState] = useState<TaskState<R>>({
        isRunning: false,
        countOfCalls: 0,
        value: initialValue,
    });

    const isInitialCall = taskState.countOfCalls === 0;

    const lastArgs = useRef<P | null>(null);

    const perform = useCallback(
        async (...args: P): Promise<R | null> => {
            if (keepLatest && taskState.isRunning) {
                lastArgs.current = args;
                return null;
            }

            if (drop && taskState.isRunning) return null;

            setTaskState((prev) => ({ ...prev, isRunning: true }));
            const result = await asyncFunction(...args);
            setTaskState((prev) => ({
                ...prev,
                isRunning: false,
                value: result,
                countOfCalls: prev.countOfCalls + 1,
            }));

            if (keepLatest && lastArgs.current) {
                lastArgs.current = null;
                return perform(...args);
            }

            return result;
        },
        [asyncFunction]
    );

    const debouncedPerform = useDebounce(perform, debounceDelay);

    return {
        ...taskState,
        isInitialCall,
        setCountOfCalls: (countOfCalls: number) => setTaskState((prev) => ({ ...prev, countOfCalls })),
        perform,
        debouncedPerform,
        setValue: (value: R | null) => setTaskState((prev) => ({ ...prev, value })),
    };
}
